import { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useSession } from '../lib/session';
import { getStripe, stripeConfigured } from '../lib/stripe';
import { fetchMyActionableCharge, fetchAuctionChargeSecret, retryMyOrder, fetchMyCards, setDefaultCard } from '../lib/queries';
import type { AuctionCharge } from '../lib/types';
import { AddCardModal } from './AddCardModal';
import { useToast } from './Toast';
import './AddCardModal.css';
import './CompleteWinningPayment.css';

/**
 * Global banner for a member whose winning-auction charge needs THEM to act
 * within the 48h window (083):
 *   - requires_action (SCA): complete the bank security check on-session;
 *   - failed (declined): update the card and retry the charge.
 * The deadline is shown so the urgency is clear. Renders nothing when there's
 * nothing to action.
 */

export function CompleteWinningPayment() {
  const { profile } = useSession();
  const toast = useToast();
  const [charge, setCharge] = useState<AuctionCharge | null>(null);
  const [open, setOpen] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const reload = useCallback(() => {
    if (!profile) { setCharge(null); return; }
    fetchMyActionableCharge()
      .then(setCharge)
      .catch(() => { /* non-fatal - just don't show the banner */ });
  }, [profile?.id]);

  useEffect(() => { reload(); }, [reload]);

  const runRetry = useCallback(async () => {
    if (!charge || retrying) return;
    setRetrying(true);
    try {
      const r = await retryMyOrder(charge.post_id);
      if (r.ok && r.status === 'succeeded') {
        toast.success('Payment complete - your lot is secured.');
      } else if (r.status === 'requires_action') {
        toast.success('Almost there - one more security check to finish.');
      } else if (r.status === 'processing') {
        toast.success('Payment processing - we’ll confirm shortly.');
      } else {
        toast.error(r.error ? `Payment failed: ${r.error}` : 'That card was declined too. Try another card.');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not retry the payment.');
    } finally {
      setRetrying(false);
      reload();
    }
  }, [charge, retrying, toast, reload]);

  if (!profile || !stripeConfigured || !charge) return null;

  const isSca = charge.status === 'requires_action';
  const deadline = countdown(charge.action_deadline);

  return (
    <>
      <div className="winpay" role="status">
        <span className="winpay__text">
          {isSca ? (
            <>You won an auction. Complete the quick bank security check to secure your lot{' '}
              ({dollars(charge.amount_cents)}).</>
          ) : (
            <>Your payment of {dollars(charge.amount_cents)} for your winning lot was declined.
              Update your card and try again to keep it.</>
          )}
          {deadline && <span className="winpay__deadline"> {deadline}</span>}
        </span>
        <span className="winpay__actions">
          {isSca ? (
            <button type="button" className="winpay__btn" onClick={() => setOpen(true)}>
              Complete payment
            </button>
          ) : (
            <>
              <button type="button" className="winpay__btn winpay__btn--ghost" onClick={() => setShowAddCard(true)}>
                Update card
              </button>
              <button type="button" className="winpay__btn" onClick={runRetry} disabled={retrying}>
                {retrying ? 'Trying…' : 'Try again'}
              </button>
            </>
          )}
        </span>
      </div>
      {open && (
        <CompleteModal
          charge={charge}
          onClose={() => setOpen(false)}
          onPaid={() => { setOpen(false); reload(); }}
        />
      )}
      {showAddCard && (
        <AddCardModal
          directNew
          postId={charge.post_id}
          onClose={() => setShowAddCard(false)}
          onLinked={async () => {
            setShowAddCard(false);
            // Make the just-added card the default so the retry (and future
            // charges) use it instead of the declined one.
            try {
              const list = await fetchMyCards();
              if (list.length) {
                const newest = list.reduce((a, b) =>
                  new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a);
                if (!newest.is_default) await setDefaultCard(newest.id);
              }
            } catch { /* fall back to default-card retry */ }
            void runRetry();
          }}
        />
      )}
    </>
  );
}

/** "2 days left to pay", "about 6h left", or "the time to pay has nearly
 *  passed" - or null when there's no deadline. */
function countdown(deadlineIso: string | null | undefined): string | null {
  if (!deadlineIso) return null;
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  if (ms <= 0) return 'The time to pay has passed.';
  const hours = ms / 3_600_000;
  if (hours >= 36) return `${Math.round(hours / 24)} days left to pay.`;
  if (hours >= 2) return `About ${Math.round(hours)}h left to pay.`;
  return 'Less than 2 hours left to pay.';
}

function CompleteModal({ charge, onClose, onPaid }: { charge: AuctionCharge; onClose: () => void; onPaid: () => void }) {
  // Lazy: Stripe.js loads only when this modal opens, not app-wide.
  const stripePromise = useMemo(() => getStripe(), []);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    fetchAuctionChargeSecret(charge.id)
      .then((cs) => { if (!cancelled) setClientSecret(cs); })
      .catch((err) => { if (!cancelled) setLoadError(err?.message ?? 'Could not load the payment.'); });
    return () => { cancelled = true; };
  }, [charge.id]);

  const onBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const options: StripeElementsOptions | undefined = clientSecret
    ? { clientSecret, appearance: { theme: 'flat', variables: { borderRadius: '10px' } } }
    : undefined;

  return (
    <div className="addcard" role="dialog" aria-modal="true" aria-label="Complete winning payment" onClick={onBackdrop}>
      <div className="addcard__panel">
        <button type="button" className="addcard__close" onClick={onClose} aria-label="Close">×</button>
        <p className="kicker addcard__kicker">Sales Room</p>
        <h2 className="addcard__title">Complete your payment</h2>
        <p className="addcard__lede">
          Your bank asked for an extra security check before this {dollars(charge.amount_cents)} payment
          could go through. Confirm it below to secure your lot.
        </p>
        {loadError && <p className="addcard__error" role="alert">{loadError}</p>}
        {!loadError && !clientSecret && <p className="addcard__loading">Loading secure payment…</p>}
        {options && (
          <Elements stripe={stripePromise} options={options}>
            <PayForm onClose={onClose} onPaid={onPaid} />
          </Elements>
        )}
      </div>
    </div>
  );
}

function PayForm({ onClose, onPaid }: { onClose: () => void; onPaid: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError(null);
    const { error: payErr } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (payErr) {
      setError(payErr.message ?? 'Payment could not be completed. Try again.');
      setBusy(false);
      return;
    }
    onPaid();
  }

  return (
    <form className="addcard__form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <p className="addcard__error" role="alert">{error}</p>}
      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--md" disabled={!stripe || busy}>
          {busy ? 'Confirming…' : 'Pay now'}
        </button>
      </div>
    </form>
  );
}

function dollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
